/**
 * Authentication Context
 * Global state management for authentication
 * Connected to TypeScript Backend API
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  User as AuthUser,
  LoginData,
  RegisterData,
  transformUserResponse,
  userWithoutRoleError,
} from '@/platform/auth'
import { authService } from '@/platform/auth/services'
import { emailInitials, emailLocalPart } from '@/platform/auth/types/ui.types'
import { changeLanguage, i18n } from '@/platform/i18n'
import { isOfferedLanguage } from '@/platform/i18n/config'
import { settingsService } from '@/platform/profile/services/settings.service'
import { userConfigService } from '@/shared/services/user-config.service'
import { BaseComponentProps } from '@/shared/types/common.types'
import type { UserConfiguration } from '@/shared/types/services.types'
import { markSessionEnding } from '@/shared/unsaved-changes/sessionEnding'
import { ApiException } from '@/shared/utils/api'
import { STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

/**
 * Clear sessionStorage entries written by ImpersonationContext /
 * ImpersonateUserModal so a logout fully kills any in-progress
 * super-admin → user impersonation. Server-side tenant impersonation
 * is ended separately by the backend logout handler (auth.service.ts
 * endActiveImpersonationForSuperAdmin) — clearing here only handles
 * the *client-held* impersonation token.
 */
const clearImpersonationSessionStorage = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_TOKEN)
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_USER)
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_ADMIN_ID)
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_EXPIRES_AT)
  } catch {
    // sessionStorage can throw in private-mode browsers; ignore — the
    // user is already being logged out, nothing else to do.
  }
}

// ============================================================================
// State Types
// ============================================================================

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitializing: boolean
  error: Error | ApiException | string | null
}

interface AuthContextValue extends AuthState {
  login: (credentials: LoginData) => Promise<boolean>
  register: (data: RegisterData) => Promise<boolean>
  logout: () => Promise<void>
  logoutAll: () => Promise<{ sessionsRevoked: number }>
  clearError: () => void
  refreshUser: () => Promise<void>
  refreshToken: () => Promise<void>
  updateUser: (updates: Partial<AuthUser>) => Promise<void>
  setAuthenticatedUser: (user: AuthUser) => void
  hasRole: (role: string) => boolean
  getUserName: () => string
  getUserInitials: () => string

  // Role helpers
  isSuperAdmin: boolean
  isOrgAdmin: boolean
  canAccessAdminPanel: boolean
  canManageTenants: boolean
  canManageSystemUsers: boolean
  canManageOrgUsers: boolean
  canViewAuditLog: boolean
  canImpersonate: boolean
}

// ============================================================================
// Action Types
// ============================================================================

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_INITIALIZING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: Error | ApiException | string | null }
  | { type: 'LOGIN_SUCCESS'; payload: AuthUser }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: AuthUser }

// ============================================================================
// Reducer
// ============================================================================

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }

    case 'SET_INITIALIZING':
      return { ...state, isInitializing: action.payload }

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false }

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        isInitializing: false,
        error: null,
      }

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitializing: false,
        error: null,
      }

    case 'UPDATE_USER':
      return { ...state, user: action.payload }

    default:
      return state
  }
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitializing: true,
  error: null,
}

// ============================================================================
// Context Creation
// ============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// ============================================================================
// Provider Component
// ============================================================================

interface AuthProviderProps extends BaseComponentProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  testId = 'auth-provider',
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const queryClient = useQueryClient()

  // ============================================================================
  // Initialize Authentication on App Start
  // ============================================================================

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'SET_INITIALIZING', payload: true })

        const result = await authService.initializeAuth()

        const initialUser = result?.user ? transformUserResponse(result.user) : null
        if (initialUser) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: initialUser })
        } else {
          // A user with no role is not a session: the service already stored one, close it.
          if (result?.user) await authService.logout()
          dispatch({ type: 'LOGOUT' })
        }
      } catch (error) {
        logger.error('Failed to initialize auth:', error)
        dispatch({ type: 'LOGOUT' })
      } finally {
        dispatch({ type: 'SET_INITIALIZING', payload: false })
      }
    }

    initializeAuth()
  }, [])

  // ============================================================================
  // Hydrate the global formatting singleton from the authenticated user's
  // persisted preferences, on login.
  //
  // Without this, a brand-new browser formats EVERY number/date with the local
  // DEFAULT_CONFIG until the user happens to open Preferences or the Balances
  // currency switcher (the only other callers of userConfigService.updateConfig)
  // — so the whole app would silently ignore the preference the backend already
  // stores.
  //
  // IMPORTANT: we source from `settingsService.getPreferences()` (GET
  // /api/user/preferences), NOT from `state.user.preferences`. The auth
  // /login and /me responses run `numberFormat` through a lossy backend code
  // map (americano-comas → 'US', europeo-puntos → 'EU', …) that the frontend
  // formatter does not understand, so hydrating from the auth user would fall
  // back to the European default. `getPreferences()` returns the RAW domain
  // vocabulary ('americano-comas', …) — the exact source PreferencesForm loads
  // — so this stays in lock-step with what the user sees on the Preferences
  // screen. We push ONLY defined fields, normalize a legacy theme 'auto' →
  // 'system', and diff against the live config to avoid redundant re-renders.
  // ============================================================================
  const authenticatedUserId = state.user?.id
  useEffect(() => {
    if (!authenticatedUserId) return

    let cancelled = false
    const hydrateFormattingPreferences = async (): Promise<void> => {
      try {
        const response = await settingsService.getPreferences()
        if (cancelled) return

        const preferences = response.preferences ?? {}
        const readString = (value: unknown): string | undefined =>
          typeof value === 'string' && value !== '' ? value : undefined

        const current = userConfigService.getConfig()
        const updates: Partial<UserConfiguration> = {}

        const numberFormat = readString(preferences.numberFormat)
        if (numberFormat !== undefined && numberFormat !== current.numberFormat) {
          updates.numberFormat = numberFormat as UserConfiguration['numberFormat']
        }
        const dateFormat = readString(preferences.dateFormat)
        if (dateFormat !== undefined && dateFormat !== current.dateFormat) {
          updates.dateFormat = dateFormat as UserConfiguration['dateFormat']
        }
        const timezone = readString(preferences.timezone)
        if (timezone !== undefined && timezone !== current.timezone) {
          updates.timezone = timezone
        }
        const rawTheme = readString(preferences.theme)
        if (rawTheme !== undefined) {
          const normalizedTheme = rawTheme === 'auto' ? 'system' : rawTheme
          if (normalizedTheme !== current.theme) {
            updates.theme = normalizedTheme as UserConfiguration['theme']
          }
        }
        // baseCurrency lives in the dedicated `reportingCurrency` field, falling
        // back to the preferences bag (mirrors PreferencesForm's load logic).
        const baseCurrency =
          readString(response.reportingCurrency) ?? readString(preferences.baseCurrency)
        if (baseCurrency !== undefined && baseCurrency !== current.baseCurrency) {
          updates.baseCurrency = baseCurrency
        }

        if (Object.keys(updates).length > 0) {
          userConfigService.updateConfig(updates)
        }

        // i18n (NOT userConfigService) drives the language the UI actually
        // renders in, so hydrate it separately: switch to the user's saved
        // language when it differs from the active one. The LanguageDetector
        // then persists the choice to localStorage. Guard on a real change to
        // avoid a redundant re-render + namespace refetch when they match.
        // ⚠️⚠️ Only a language the platform still OFFERS. Measured in e2e: with
        // «English» deactivated, a user whose profile said `en` was switched
        // back to it on every login — the default language the deactivation
        // dialog promised never took over, and `en` was re-written to
        // localStorage so the next boot chose it again.
        const language = readString(preferences.language)
        // No language in the profile yet: the one this screen is shown in becomes it, so what
        // the backend renders for this person (notifications, mail) speaks it too.
        if (language === undefined && isOfferedLanguage(i18n.language)) {
          await settingsService.updatePreferences({ language: i18n.language })
        }
        if (language !== undefined && language !== i18n.language && isOfferedLanguage(language)) {
          await changeLanguage(language)
          // Persist so the next boot's getSavedLanguage() reads the user's
          // language (no flash of the browser-default language) and screen
          // readers get the right document lang. Mirrors the LanguageSelector
          // path (useTranslation.changeLanguage) — the exported changeLanguage
          // primitive itself does NOT persist.
          if (typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEYS.LANGUAGE, language)
            document.documentElement.lang = language
          }
        }
      } catch (error) {
        // Non-fatal: the singleton keeps its defaults and Preferences/Balances
        // will hydrate it on demand. Benign aborts are already demoted to debug
        // inside settingsService.getPreferences().
        logger.debug('Failed to hydrate formatting preferences on login:', error)
      }
    }

    void hydrateFormattingPreferences()
    return () => {
      cancelled = true
    }
  }, [authenticatedUserId])

  // ============================================================================
  // Auth Actions
  // ============================================================================

  const login = useCallback(async (credentials: LoginData): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'SET_ERROR', payload: null })

      const response = await authService.login(credentials)

      const signedIn = response.user ? transformUserResponse(response.user) : null
      if (signedIn) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: signedIn })
        return true
      }
      if (response.user) {
        // A user with no role is not a session: the service already stored one, close it.
        await authService.logout()
        throw userWithoutRoleError()
      }
      throw new Error('Login response missing user data')
    } catch (error) {
      // Store the complete error object to preserve details
      dispatch({ type: 'SET_ERROR', payload: error as Error | ApiException | string })
      throw error // Re-throw for component handling
    }
  }, [])

  const register = useCallback(
    async (data: RegisterData): Promise<boolean> => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true })
        dispatch({ type: 'SET_ERROR', payload: null })

        await authService.register(data)

        // Auto-login after successful registration
        // If login fails due to email verification, registration is still successful
        try {
          const loginSuccess = await login({ email: data.email, password: data.password })
          return loginSuccess
        } catch (loginError: unknown) {
          // If login failed because email verification is required, that's expected behavior
          // The registration was successful, but user needs to verify email first
          const loginErrObj = loginError as Record<string, unknown> | null
          logger.info('AuthContext register - Login error:', {
            code: loginErrObj?.['code'],
            errorCode: (loginErrObj?.['error'] as Record<string, unknown> | undefined)?.['code'],
            message: loginErrObj?.['message'],
          })
          if (
            loginErrObj?.['code'] === 'EMAIL_NOT_VERIFIED' ||
            (loginErrObj?.['error'] as Record<string, unknown> | undefined)?.['code'] ===
              'EMAIL_NOT_VERIFIED'
          ) {
            // Clear the error from state since this is expected behavior
            dispatch({ type: 'SET_ERROR', payload: null })
            // Registration successful, but email verification required
            return true
          }
          // For other login errors, re-throw
          throw loginError
        }
      } catch (error) {
        // Store the complete error object to preserve details
        dispatch({ type: 'SET_ERROR', payload: error as Error | ApiException | string })
        throw error // Re-throw for component handling
      }
    },
    [login],
  )

  const logout = useCallback(async () => {
    // Before anything else: the redirects of a sign-out never meet the unsaved-changes dialog.
    markSessionEnding()
    try {
      dispatch({ type: 'SET_LOADING', payload: true })

      await authService.logout()
      // Clear React Query cache to prevent cross-tenant data leakage
      queryClient.clear()
      // Drop any client-held impersonation token so the banner does
      // not reappear after re-login with cached sessionStorage state.
      clearImpersonationSessionStorage()
      dispatch({ type: 'LOGOUT' })
    } catch (error) {
      logger.error('Logout error:', error)
      // Always clear local state even if server call fails
      queryClient.clear()
      clearImpersonationSessionStorage()
      dispatch({ type: 'LOGOUT' })
    }
  }, [queryClient])

  /**
   * Logout from ALL devices/sessions for the current user.
   *
   * Mirrors the resilient pattern of `logout`: even when the backend call
   * fails, we still clear React Query cache and flip auth state to logged-out
   * locally. The user explicitly asked to be signed out everywhere — getting
   * stuck "logged in" on the current device would violate that intent.
   *
   * Returns the number of sessions revoked by the server so the calling UI
   * can surface a confirmation message ("Logged out from N devices").
   */
  const logoutAll = useCallback(async (): Promise<{ sessionsRevoked: number }> => {
    markSessionEnding()
    try {
      dispatch({ type: 'SET_LOADING', payload: true })

      const result = await authService.logoutAll()
      // Clear React Query cache to prevent cross-tenant data leakage
      queryClient.clear()
      // Drop any client-held impersonation token (see logout).
      clearImpersonationSessionStorage()
      dispatch({ type: 'LOGOUT' })
      return result
    } catch (error) {
      logger.error('Logout-all error:', error)
      // Always clear local state even if server call fails
      queryClient.clear()
      clearImpersonationSessionStorage()
      dispatch({ type: 'LOGOUT' })
      throw error
    }
  }, [queryClient])

  const refreshUser = useCallback(async () => {
    try {
      const user = await authService.getCurrentUser()
      const refreshed = user ? transformUserResponse(user) : null
      if (refreshed) {
        dispatch({ type: 'UPDATE_USER', payload: refreshed })
      } else if (user) {
        // A user with no role is not a session (the service already stored it): the same as at the start. `logout`
        // closes it on the server, clears the cache and the impersonation and leaves the state signed out.
        await logout()
      }
    } catch (error) {
      logger.error('Failed to refresh user:', error)
      // If refresh fails, user might need to re-login
      if (error instanceof ApiException && error.status === 401) {
        dispatch({ type: 'LOGOUT' })
      }
    }
  }, [logout])

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null })
  }, [])

  const refreshToken = useCallback(async () => {
    try {
      const refreshedData = await authService.refreshToken()
      const refreshed = refreshedData.user ? transformUserResponse(refreshedData.user) : null
      if (refreshed) {
        dispatch({ type: 'UPDATE_USER', payload: refreshed })
      } else if (refreshedData.user) {
        // A user with no role is not a session (the service already stored it): the same as at the start.
        await logout()
        throw userWithoutRoleError()
      }
    } catch (error) {
      logger.error('Token refresh failed:', error)
      dispatch({ type: 'LOGOUT' })
      throw error
    }
  }, [logout])

  const setAuthenticatedUser = useCallback((authenticatedUser: AuthUser) => {
    dispatch({ type: 'LOGIN_SUCCESS', payload: authenticatedUser })
  }, [])

  const updateUser = useCallback(
    async (updates: Partial<AuthUser>) => {
      if (!state.user) {
        throw new Error('No user to update')
      }

      try {
        const updatedUser = { ...state.user, ...updates }
        dispatch({ type: 'UPDATE_USER', payload: updatedUser })
      } catch (error) {
        logger.error('Failed to update user:', error)
        throw error
      }
    },
    [state.user],
  )

  const hasRole = useCallback(
    (role: string): boolean => {
      return state.user?.role === role || false
    },
    [state.user?.role],
  )

  const getUserName = useCallback((): string => {
    // The account keeps no names: the UI names the user by the email's local part.
    if (!state.user?.email) return 'User'
    return emailLocalPart(state.user.email)
  }, [state.user])

  const getUserInitials = useCallback((): string => {
    return emailInitials(state.user?.email)
  }, [state.user])

  // ============================================================================
  // Role Helpers - Memoized to prevent unnecessary re-renders
  // ============================================================================

  const roleHelpers = useMemo(() => {
    const isSuperAdmin = state.user?.role === 'super_admin'
    const isOrgAdmin = state.user?.role === 'super_admin' || state.user?.role === 'org_admin'
    return {
      isSuperAdmin,
      isOrgAdmin,
      canAccessAdminPanel: isOrgAdmin,
      canManageTenants: isSuperAdmin,
      canManageSystemUsers: isSuperAdmin,
      canManageOrgUsers: isOrgAdmin,
      canViewAuditLog: isOrgAdmin,
      canImpersonate: isSuperAdmin,
    }
  }, [state.user?.role])

  // ============================================================================
  // Context Value - Memoized to prevent unnecessary re-renders
  // ============================================================================

  const value: AuthContextValue = useMemo(
    () => ({
      ...state,
      login,
      register,
      logout,
      logoutAll,
      clearError,
      refreshUser,
      refreshToken,
      updateUser,
      setAuthenticatedUser,
      hasRole,
      getUserName,
      getUserInitials,
      ...roleHelpers,
    }),
    [
      state,
      login,
      register,
      logout,
      logoutAll,
      clearError,
      refreshUser,
      refreshToken,
      updateUser,
      setAuthenticatedUser,
      hasRole,
      getUserName,
      getUserInitials,
      roleHelpers,
    ],
  )

  return (
    <div data-testid={testId}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </div>
  )
}

// ============================================================================
// Hook for Using Auth Context
// ============================================================================

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
